import {FC, forwardRef, RefAttributes, useCallback, useEffect, useImperativeHandle, useRef} from "react";
import * as fabric from "fabric";
import {useWindowSize} from "../../../hooks/useWindowSize.ts";

export type CanvasHandle = {
    save: () => void
}

export type CanvasProps = RefAttributes<CanvasHandle> & {
    qrCodeImage: fabric.FabricImage
    mainImage: fabric.FabricImage
}

const Canvas: FC<CanvasProps> = forwardRef<CanvasHandle, CanvasProps>(({qrCodeImage, mainImage}, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const anchorRef = useRef<HTMLAnchorElement>(null);
    const fabricCanvas = useRef<fabric.StaticCanvas>(null);
    const fabricTriangle = useRef<fabric.Rect>(null)

    useEffect(() => {
        if (!canvasRef.current || !containerRef.current) {
            return
        }
        const imageScale = fabric.util.findScaleToFit(mainImage, {
            width: containerRef.current.offsetWidth ?? 0,
            height: containerRef.current.offsetHeight ?? 0
        })
        mainImage.scale(imageScale)
        const imageWidth = mainImage.width * imageScale
        const imageHeight = mainImage.height * imageScale

        fabricCanvas.current = new fabric.StaticCanvas(canvasRef.current, {
            width: imageWidth,
            height: imageHeight,
            renderOnAddRemove: false,
        });
        setTimeout(() => {
            if (!canvasRef.current) {
                return
            }
            fabricCanvas.current?.add(mainImage);
            fabricCanvas.current?.centerObject(mainImage);

            const qrCodeSize = Math.min(mainImage.getScaledWidth(), mainImage.getScaledHeight()) * 0.2;
            const triangleLegSize = qrCodeSize * 2 + 20;
            const triangleHypothenuseSize = Math.sqrt(Math.pow(triangleLegSize, 2)*2)

            fabricTriangle.current = new fabric.Rect(
                {
                    width: triangleHypothenuseSize,
                    height: triangleHypothenuseSize,
                    angle: 45,
                    top: mainImage.top + mainImage.getScaledHeight()-triangleLegSize,
                    left: mainImage.left,
                    fill: 'rgba(255, 255, 255, 0.7)',
                },
            )
            fabricCanvas.current?.add(fabricTriangle.current);

            qrCodeImage.scaleToWidth(qrCodeSize);
            qrCodeImage.set({
                left: 5,
                top: mainImage.getScaledHeight() - qrCodeImage.getScaledHeight() - 5,
                hasBorders: false,
                selectable: false,
            })
            fabricCanvas.current?.add(qrCodeImage)

            fabricCanvas.current?.renderAndReset();
        }, 10)

        return () => {
            fabricCanvas.current?.dispose();
            fabricCanvas.current = null
        }
    }, [mainImage, qrCodeImage]);

    useWindowSize(() => {
        if (containerRef.current && fabricCanvas.current) {
            const imageScale = fabric.util.findScaleToFit(mainImage, {
                width: containerRef.current.offsetWidth ?? 0,
                height: containerRef.current.offsetHeight ?? 0
            })
            mainImage.scale(imageScale)
            const imageWidth = mainImage.width * imageScale
            const imageHeight = mainImage.height * imageScale

            fabricCanvas.current.setDimensions({
                width: imageWidth,
                height: imageHeight,
            })
            fabricCanvas.current.centerObject(mainImage);

            const qrCodeSize = Math.min(mainImage.getScaledWidth(), mainImage.getScaledHeight()) * 0.2;
            const triangleLegSize = qrCodeSize * 2 + 20;
            const triangleHypothenuseSize = Math.sqrt(Math.pow(triangleLegSize, 2)*2)

            fabricTriangle.current?.set({
                width: triangleHypothenuseSize,
                height: triangleHypothenuseSize,
                top: mainImage.top + mainImage.getScaledHeight() - triangleLegSize,
                left: mainImage.left,
            })

            qrCodeImage.scaleToWidth(qrCodeSize);
            qrCodeImage.set({
                left: mainImage.left! + 5,
                top: mainImage.top! + mainImage.getScaledHeight() - qrCodeImage.getScaledHeight() - 5,
                hasBorders: false,
                selectable: false,
            })

            fabricCanvas.current.renderAll();
        }
    })

    const handleSave = useCallback(() => {
        if (fabricCanvas.current && anchorRef.current) {
            anchorRef.current.href = fabricCanvas.current.toDataURL({
                format: 'jpeg',
                quality: 1.0,
                multiplier: mainImage.height / mainImage.getScaledHeight(),
                left: mainImage.left,
                top: mainImage.top,
                width: mainImage.getScaledWidth(),
                height: mainImage.getScaledHeight(),
            });
            anchorRef.current.download = 'photo_with_qr.jpeg';
            anchorRef.current.click();
        }
    }, [mainImage])

    useImperativeHandle(ref, () => ({
        save: handleSave
    }), [handleSave]);

    return (
        <div ref={containerRef} style={{width: "100%", height: "100%", overflow: "hidden", display: "flex", justifyContent: "center"}}>
            <canvas ref={canvasRef}/>
            <a ref={anchorRef} style={{display: 'none'}}/>
        </div>
    )
})

export default Canvas
